import { Tooltip } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { engineFuelDetails, getLabel } from 'src/modules/labels';
import theme from 'src/setup/theme';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  fuelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  fuelCount: {
    fontSize: 11,
    fontWeight: 500,
    color: grey[600],
    flexShrink: 0,
    minWidth: 16,
  },
  fuelLabel: {
    fontSize: 12,
    color: grey[800],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  moreFuels: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    fontSize: 10,
    fontWeight: 500,
    color: theme.palette.primary.main,
    backgroundColor: grey[200],
    borderRadius: 4,
    padding: '1px 4px',
    cursor: 'default',
  },
});

const MAX_FUELS_DISPLAYED = 3;

interface PackageFuelCellProps {
  fuels: { [key: string]: number };
}

export const PackageFuelCell = ({ fuels }: PackageFuelCellProps): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation();

  const fuelEntries = useMemo(
    () =>
      Object.entries(fuels)
        .map(([fuel, count]) => ({
          key: fuel,
          label: getLabel(engineFuelDetails, fuel) ?? fuel,
          count,
        }))
        .sort((a, b) => b.count - a.count),
    [fuels],
  );

  const displayedFuels = fuelEntries.slice(0, MAX_FUELS_DISPLAYED);
  const remainingFuels = fuelEntries.slice(MAX_FUELS_DISPLAYED);
  const remainingFuelsTooltip = remainingFuels.map((f) => `${f.label} (${f.count})`).join(', ');

  return (
    <div className={classes.container}>
      {displayedFuels.map((fuel) => (
        <div key={fuel.key} className={classes.fuelRow}>
          <span className={classes.fuelCount}>{fuel.count}</span>
          <Tooltip title={fuel.label} arrow>
            <span className={classes.fuelLabel}>{fuel.label}</span>
          </Tooltip>
        </div>
      ))}
      {remainingFuels.length > 0 && (
        <Tooltip title={remainingFuelsTooltip} arrow>
          <span className={classes.moreFuels}>
            +{remainingFuels.length} {t('car.fuel', { count: remainingFuels.length })}
          </span>
        </Tooltip>
      )}
    </div>
  );
};
